import * as React from 'react';
import * as $ from 'jquery';
import * as _ from 'underscore';
const compareObjects = require('fast-deep-equal');

import { IGlob } from '../ts/Glob';
import { useOverflowHook } from '../../../_common/platform/tsx/mixins/OverflowMixin';
import { getStyles, useResizeAndMoveHook } from '../../../_common/platform/tsx/mixins/ResizeMove';
import { config } from '../ts/config';

import StringField from '../../../_common/platform/tsx/StringField';
import BooleanField from '../../../_common/platform/tsx/BooleanField';
import { DirsTree, IExpandedItems } from './DirsTree';
import { DirsWorkspacesSingle } from './DirsWorkspacesSingle';
import { IDirContentItem } from '../ts/stores/WorkspacesDirsStore';

const DEFAULT_ID = 'default';

export interface IDirsWorkspacesInnerProperties {
    id?: string;
    name?: string;
    title: string;
    selectTitle: string;
    dir: number;
    workspace?;
    expandedItems?: IExpandedItems;
    plainMode?: boolean;
    hiddenScroll?: boolean;
    overwriteShow?: boolean;
    nameSelect: boolean;
    showExtendedDirs?: boolean;
    showMyFolders: boolean;
    immediateSelect?: boolean;
    overwrite?: boolean;
    allowSelectItem: boolean;
    allowOpenWorkspace: boolean;
    multipleWorkspacesSelectMode?: boolean;
    modeSelectWorkspace?: boolean;
    zIndex?: number;
    loading?: boolean;
    disableOverflow?: boolean;
    glob: IGlob;

    onDirSelect?(id: string, name: string, overwrite: boolean): void;
    onHide?(e): void;
    onWorkspaceSelect?(dirSelect, value, workspaceName: string, selectedName: string): void;
}

interface IInstanceState {
    folderID: number;
    loading: boolean;
    content: IDirContentItem[];
}

interface IState {
    selectedWorkspace;
    workspaceName: string;
    selectedName: string;
}

function getInstanceState(id: string, folderID: number, glob: IGlob): IInstanceState {
    if (folderID == null) {
        return { folderID: undefined, content: undefined, loading: undefined };
    }

    const state = glob.WorkspacesDirsStore.getInstanceState(id ?? DEFAULT_ID);

    return {
        folderID: folderID,
        content: state.dirContentHash[folderID.toString()],
        loading: state.loadingFolders[folderID.toString()]
    };
}

function useFolderStateHook(
    props: IDirsWorkspacesInnerProperties
): [IInstanceState, (id: string, folderID: number) => void] {
    const state = React.useRef(getInstanceState(props.id, props.dir, props.glob));
    const [, setRefreshKey] = React.useState(0);

    const setFolderState = React.useCallback((id: string, folderID: number) => {
        const newState = getInstanceState(id, folderID, props.glob);

        if (!compareObjects(newState, state.current)) {
            state.current.folderID = newState.folderID;
            state.current.content = newState.content;
            state.current.loading = newState.loading;
            setRefreshKey((refreshKey) => ++refreshKey);
        }
    }, []);

    return [state.current, setFolderState];
}

function workspacesExplorerSingleLoad(folderID: number, id: string, glob: IGlob) {
    if (folderID != null) {
        glob.AppActions.workspacesExplorerSingleLoad(folderID, id ?? DEFAULT_ID);
    }
}

export function DirsWorkspacesInner(props: IDirsWorkspacesInnerProperties) {
    const [folderState, setFolderState] = useFolderStateHook(props);
    const [overwrite, setOverwrite] = React.useState(props.overwrite ?? false);
    const [state, setState] = React.useState<IState>({
        selectedWorkspace: props.workspace,
        workspaceName: props.name,
        selectedName: null
    });

    const containerRef = React.useRef<HTMLDivElement>(null);
    const contentRef = React.useRef<HTMLDivElement>(null);
    const headerRef = React.useRef<HTMLDivElement>(null);
    const footerRef = React.useRef<HTMLDivElement>(null);

    const { containerSize, initialContainerHeight, initialContentWidth, position } = useResizeAndMoveHook(
        {
            dialogName: 'SaveAs',
            draggable: true,
            resizable: true,
            disableOverflow: props.disableOverflow
        },
        'auto',
        '1/2',
        contentRef,
        headerRef,
        footerRef
    );
    const containerHeight = useOverflowHook(
        props.disableOverflow,
        containerSize.height,
        containerRef,
        headerRef,
        footerRef
    );

    React.useEffect(() => {
        const handleOnStoreChange = () => {
            setFolderState(props.id, folderState.folderID);
        };

        props.glob.WorkspacesDirsStore.addChangeListener(handleOnStoreChange);

        const id = props.id ?? DEFAULT_ID;

        props.glob.AppActions.workspacesExplorerClear(id);
        workspacesExplorerSingleLoad(folderState.folderID, id, props.glob);

        return () => {
            props.glob.WorkspacesDirsStore.removeChangeListener(handleOnStoreChange);
        };
    }, [props.id]);

    React.useEffect(() => {
        if (!_.isEqual(state.selectedWorkspace, props.workspace)) {
            setState({
                ...state,
                selectedWorkspace: props.workspace,
                selectedName: null
            });
        }
    }, [props.workspace]);

    React.useEffect(() => {
        if (folderState.folderID !== props.dir) {
            setFolderState(props.id, props.dir);
            workspacesExplorerSingleLoad(props.dir, props.id, props.glob);
        }
    }, [props.dir, props.id]);

    const handleOnClose = (e) => {
        if (typeof props.onHide === 'function') {
            props.onHide(e);
        } else {
            props.glob.mainRouter.navigate('/', { trigger: true });
        }
    };

    const handleOnFolderSelect = (id: number) => {
        setFolderState(props.id, id);
        workspacesExplorerSingleLoad(id, props.id, props.glob);

        if (props.immediateSelect) {
            processFolderSelect(id);
        }
    };

    const handleOnWorkspaceSelect = (value, name) => {
        setState({
            ...state,
            selectedWorkspace: value,
            selectedName: name,
            workspaceName: name //VR: save it too; it means: selecting WS item pasted its name to 'NAME" field.
        });

        if (props.immediateSelect) {
            processWorkspaceSelect(null, value, name);
        }
    };

    const getContent = () => {
        const { content, loading, folderID } = folderState;

        return (
            <div className="row">
                <div className="dirs-tree col-sm-4">
                    <DirsTree
                        id={props.id ?? DEFAULT_ID}
                        glob={props.glob}
                        rootId={config.WIDGETS_ROOT_DIR_ID}
                        rootName={config.WIDGETS_ROOT_DIR_NAME}
                        showExtendedDirs={props.showExtendedDirs}
                        showMyFolders={props.showMyFolders}
                        collapsed={false}
                        expandedItems={props.expandedItems || {}}
                        select={folderID}
                        onSelect={handleOnFolderSelect}
                    />
                </div>

                <div className="dirs-workspaces col-sm-8">
                    <DirsWorkspacesSingle
                        content={content}
                        loading={loading || props.loading}
                        select={state.selectedWorkspace}
                        multipleSelectMode={props.multipleWorkspacesSelectMode}
                        allowSelect={props.allowSelectItem}
                        onSelect={handleOnWorkspaceSelect}
                    />
                </div>
            </div>
        );
    };

    // const processNewWorkspace = (e) => {
    //     props.glob.mainRouter.navigate('/workspaces/new', {
    //         trigger: true
    //     });
    // };

    const checkIfWorkspaceExists = (name: string) => {
        if (overwrite) {
            // VR: if User set 'overwrite ON", then don't make searching!
            return null;
        }

        const { content } = folderState;

        if (content != null) {
            return (
                content.find((obj) => {
                    return obj.name === name;
                }) != null
            );
        }

        return false;
    };

    const passSelection = (id) => {
        if (typeof props.onDirSelect === 'function') {
            props.onDirSelect(id || folderState.folderID, state.workspaceName, overwrite);
        }
    };

    const processFolderSelect = (id) => {
        const name = state.workspaceName;

        if (name === '') {
            props.glob.AppActions.showNotification_Fail('Workspace name cannot be empty');
            return;
        }

        const exists = checkIfWorkspaceExists(name);

        if (exists) {
            let interval: NodeJS.Timer;

            props.glob.DialogActions.createDialog(
                'Overwrite workspace "' + name + '"',
                "Workspace with name '" + name + "' already exists in selected folder. Do you want to overwrite it?",
                () => {},
                () => {
                    setOverwrite(true);
                    // VR: we need go out of this dialog and afterwards: set def.resolve() to close this dialog:
                    interval = setInterval(() => {
                        clearInterval(interval);
                        passSelection(id);
                    }, 200);
                },
                () => {},
                'confirm',
                {
                    confirmTitle: null,
                    cancelTitle: 'Overwrite'
                }
            );
        } else {
            passSelection(id);
        }
    };

    const processWorkspaceSelect = (e, value, selectedName) => {
        if (props.modeSelectWorkspace) {
            passSelection(value);
        } else if (typeof props.onWorkspaceSelect === 'function') {
            props.onWorkspaceSelect(
                folderState.folderID,
                value || state.selectedWorkspace,
                state.workspaceName,
                selectedName || state.selectedName
            );
        }
    };

    let openButton: React.JSX.Element;
    let selectButton: React.JSX.Element;

    if (props.allowOpenWorkspace && state.selectedWorkspace != null) {
        openButton = (
            <button
                type="button"
                className="btn btn-success"
                onClick={(e) => {
                    processWorkspaceSelect(e, state.selectedWorkspace, state.selectedName);
                }}
            >
                {'Open selected workspace'}
            </button>
        );
    }

    if (props.modeSelectWorkspace) {
        if (state.selectedWorkspace != null) {
            selectButton = (
                <button
                    type="button"
                    className="btn btn-success"
                    onClick={(e) => {
                        processWorkspaceSelect(e, state.selectedWorkspace, state.selectedName);
                    }}
                >
                    {props.selectTitle || 'Select'}
                </button>
            );
        }
    } else if (folderState.folderID != null && folderState.loading !== true) {
        const disabled =
            props.allowSelectItem == true && (state.workspaceName == null || state.workspaceName.trim().length === 0);

        selectButton = (
            <button
                type="button"
                className="btn btn-success"
                disabled={disabled}
                onClick={(e) => {
                    processFolderSelect(folderState.folderID);
                }}
            >
                {props.selectTitle || 'Create workspace in selected folder'}
            </button>
        );
    }

    let nameSelect: React.JSX.Element;

    if (props.nameSelect) {
        const handleOnEnterPress = (value) => {
            processFolderSelect(folderState.folderID);
        };

        const handleOnNameChange = (value: string) => {
            setState({
                ...state,
                workspaceName: value
            });
        };

        nameSelect = (
            <div className="name-select col-8">
                <StringField
                    value={state.workspaceName}
                    onEnterPress={handleOnEnterPress}
                    onChange={handleOnNameChange}
                />
            </div>
        );
    }

    let overwriteCheckbox: React.JSX.Element;
    let createSubDir: React.JSX.Element;
    let className = '';

    if (props.overwriteShow) {
        const handleOnOverwriteChange = (value: boolean) => {
            setOverwrite(value);
        };

        const handleOnaddNewFilterClick = () => {
            if (folderState.folderID != null) {
                props.glob.AppActions.dirsCreateStart(folderState.folderID, props.id ?? DEFAULT_ID);
            } else {
                props.glob.AppActions.showNotification_Success('Select a folder for operation, please');
            }
        };

        className = ' modal-header-narrow';

        overwriteCheckbox = (
            <div className="overwrite-checkbox col-4">
                <BooleanField value={overwrite} title={'Replace Existing'} onChange={handleOnOverwriteChange} />
            </div>
        );

        createSubDir = (
            <div
                key={'new-dir-button'}
                className="add-button btn btn-default btn-xs bar-btn sub-dir-button"
                onClick={handleOnaddNewFilterClick}
            >
                <span className="glyphicon glyphicon-plus"></span>
                &nbsp;{'New Folder'}&nbsp;
            </div>
        );
    }

    if (props.plainMode) {
        return <div className="popup-dirs">{getContent()}</div>;
    }

    const { modalStyle, containerStyle, contentStyle, bodyStyle } = getStyles(
        initialContainerHeight,
        initialContentWidth,
        containerSize.height,
        containerHeight,
        position,
        config.LAYOUT_TYPE === 'narrow',
        undefined,
        props.zIndex
    );

    return (
        <div className="modal fade show popup-dirs" style={modalStyle} role="dialog">
            <div className="modal-dialog" style={containerStyle} ref={containerRef}>
                <div className="modal-content" style={contentStyle} ref={contentRef}>
                    <div className={'modal-header' + className} ref={headerRef}>
                        <h4 className="modal-title">{props.title ?? 'Workspace Explorer'}</h4>

                        {createSubDir}

                        <button type="button" className="close" onClick={handleOnClose}>
                            &times;
                        </button>
                    </div>

                    <div className="modal-body container-fluid" style={bodyStyle}>
                        {getContent()}
                    </div>

                    <div className="modal-footer" ref={footerRef}>
                        <div className="row flex-grow-1 align-items-center">
                            {nameSelect}
                            {overwriteCheckbox}
                        </div>

                        {selectButton}
                        {openButton}

                        <button type="button" className="btn btn-default" onClick={handleOnClose}>
                            {'Cancel'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
